import { useState } from "react";
import { api, formatApiErrorDetail, pollJob, jobErrorMessage, newIdemKey, idemHeaders } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const LIST_FIELDS = ["content_pillars", "approved_terminology", "prohibited_terminology"];
const LABELS = {
  primary_audience: "Primary Audience", secondary_audience: "Secondary Audience",
  brand_voice: "Brand Voice", value_proposition: "Value Proposition",
  content_pillars: "Content Pillars", approved_terminology: "Approved Terminology",
  prohibited_terminology: "Prohibited Terminology", clinical_compliance_rules: "Compliance Rules",
  default_cta: "Default CTA", default_cta_keyword: "Default CTA Keyword",
};

export default function SetupAssistantDialog({ open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [values, setValues] = useState({});

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/marketing/ai/setup-assistant", null, idemHeaders(newIdemKey()));
      const job = await pollJob(data.job_id);
      if (job.status === "Failed" || job.status === "Cancelled") { toast.error(jobErrorMessage(job)); return; }
      const result = job.result || {};
      setProposals(result);
      const v = {};
      Object.entries(result.proposed || {}).forEach(([k, val]) => { v[k] = LIST_FIELDS.includes(k) ? (val || []).join("\n") : (val || ""); });
      setValues(v);
      setAccepted({});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Setup Assistant failed"); }
    finally { setLoading(false); }
  };

  const acceptAll = () => { const a = {}; Object.keys(proposals.proposed).forEach((k) => (a[k] = true)); setAccepted(a); };

  const save = async () => {
    const payload = {};
    Object.keys(accepted).filter((k) => accepted[k]).forEach((k) => {
      payload[k] = LIST_FIELDS.includes(k) ? values[k].split("\n").map((s) => s.trim()).filter(Boolean) : values[k];
    });
    if (Object.keys(payload).length === 0) { toast.error("Accept at least one proposal"); return; }
    setLoading(true);
    try { await api.put("/marketing/brand-profile", payload); toast.success("Brand profile updated"); onOpenChange(false); setProposals(null); onSaved && onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setProposals(null); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="setup-assistant-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Wand2 className="h-5 w-5 text-[#581C87]" /> AI Setup Assistant</DialogTitle>
          <DialogDescription>Proposes brand profile improvements from your existing records. Review before saving. It never invents credentials, accreditation, outcomes, or claims.</DialogDescription>
        </DialogHeader>

        {!proposals ? (
          <div className="py-6 text-center">
            <p className="text-sm text-gray-500 mb-4">Generate proposals for missing or weak brand fields.</p>
            <Button onClick={run} disabled={loading} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="run-setup-assistant-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate Proposals"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="flex justify-end"><Button variant="outline" size="sm" onClick={acceptAll} data-testid="accept-all-button">Accept All</Button></div>
            {Object.keys(proposals.proposed).map((k) => (
              <div key={k} className="rounded-lg border border-gray-200 p-4" data-testid={`proposal-${k}`}>
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">{LABELS[k] || k}</Label>
                  <label className="flex items-center gap-2 text-xs text-gray-500">
                    <input type="checkbox" checked={!!accepted[k]} onChange={(e) => setAccepted((s) => ({ ...s, [k]: e.target.checked }))} className="accent-[#581C87]" data-testid={`accept-${k}`} />
                    Accept
                  </label>
                </div>
                <p className="text-xs text-gray-400 mt-2">Current: {Array.isArray(proposals.current[k]) ? (proposals.current[k] || []).join(", ") : (proposals.current[k] || "—")}</p>
                {LIST_FIELDS.includes(k)
                  ? <Textarea value={values[k]} onChange={(e) => setValues((s) => ({ ...s, [k]: e.target.value }))} className="mt-2 focus-visible:ring-[#581C87]" placeholder="One per line" />
                  : <Input value={values[k]} onChange={(e) => setValues((s) => ({ ...s, [k]: e.target.value }))} className="mt-2 focus-visible:ring-[#581C87]" />}
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          {proposals && <Button onClick={save} disabled={loading} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="save-proposals-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Accepted"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
